import React from "react";

const BAND = {
  good: { ring: "text-green-600", pill: "bg-green-50 text-green-700 ring-green-600/20" },
  warn: { ring: "text-amber-500", pill: "bg-amber-50 text-amber-700 ring-amber-600/20" },
  bad: { ring: "text-red-600", pill: "bg-red-50 text-red-700 ring-red-600/20" },
};

const SEVERITY = {
  high: "bg-red-50 text-red-700 ring-red-600/20",
  medium: "bg-amber-50 text-amber-700 ring-amber-600/20",
  low: "bg-gray-50 text-gray-600 ring-gray-500/20",
};

function ScoreRing({ score, band }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * c;
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" role="img" aria-label={`InterroPol score ${score} out of 100`} className={BAND[band]?.ring}>
      <circle cx="32" cy="32" r={r} fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="6" />
      <circle cx="32" cy="32" r={r} fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round"
        strokeDasharray={`${dash} ${c}`} transform="rotate(-90 32 32)" />
      <text x="32" y="37" textAnchor="middle" className="fill-gray-900" style={{ fontSize: 16, fontWeight: 600 }}>
        {score}
      </text>
    </svg>
  );
}

/**
 * InterroPol headline in the app report's Privacy section: score, verdict counts,
 * a one-line summary, the top red flags and contradictions, and a link to the
 * full assessment. Renders nothing when there is no assessment (the API also
 * returns null when the organisation is not subscribed to InterroPol).
 */
export default function InterropolSummary({ interropol }) {
  if (!interropol || interropol.score == null) return null;
  const { score, grade, band, counts, summary, redFlags = [], redFlagCount = 0, contradictions = [], assessedAt, link } = interropol;
  const assessed = assessedAt ? new Date(assessedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;

  return (
    <div className="col-span-4 mt-6 rounded-lg border border-gray-200 p-4" data-testid="interropol-summary">
      <div className="flex items-start gap-4">
        <ScoreRing score={score} band={band} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900">InterroPol privacy assessment</h4>
            <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${BAND[band]?.pill || ""}`}>
              Grade {grade}
            </span>
            {assessed && <span className="text-xs text-gray-500">Assessed {assessed}</span>}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            How well the privacy policy matches what the app actually does: {counts.supported} supported,{" "}
            {counts.notEvidenced} not evidenced, <span className={counts.contradicted ? "font-medium text-red-600" : ""}>{counts.contradicted} contradicted</span>.
          </p>
          {summary && <p className="mt-2 text-sm text-gray-700">{summary}</p>}
        </div>
      </div>

      {(redFlags.length > 0 || contradictions.length > 0) && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {redFlags.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Red flags in the policy</h5>
              <ul className="mt-2 space-y-1.5">
                {redFlags.map((f) => (
                  <li key={f.title} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className={`mt-0.5 inline-flex shrink-0 rounded px-1.5 text-[10px] font-semibold uppercase ring-1 ring-inset ${SEVERITY[f.severity] || SEVERITY.low}`}>
                      {f.severity}
                    </span>
                    <span>{f.title}</span>
                  </li>
                ))}
              </ul>
              {redFlagCount > redFlags.length && <p className="mt-1 text-xs text-gray-500">+{redFlagCount - redFlags.length} more</p>}
            </div>
          )}
          {contradictions.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Policy vs. reality</h5>
              <ul className="mt-2 space-y-1.5">
                {contradictions.map((c) => (
                  <li key={c.statement} className="text-sm text-gray-700">
                    <span className="italic">“{c.statement}”</span>
                    {c.reason && <span className="text-red-600"> — {c.reason}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {link && (
        <div className="mt-4">
          <a href={link} className="text-sm font-medium text-mobstr-purple hover:underline">
            View the full InterroPol assessment →
          </a>
        </div>
      )}
    </div>
  );
}
